/**
 * The pinned TanStack Query key shape for every Azure Storage listing (MASTER
 * "Contracts to pin before S8", spec §5.1):
 *
 *   ['azure-storage', connectionId, containerScopeKey(ref), storageResourceKey(ref), fingerprint]
 *
 * The third element lets one `containerScopeKey` prefix invalidate the Blob
 * and the HNS view of the same container; the second lets a connection reset
 * drop everything the connection owns. Tree (`azureStorageListing.ts`) and
 * grid (`useStorageListing`) MUST build their keys here so a shared key
 * always means a shared cache shape.
 */
import {
  containerScopeKey,
  storageResourceKey,
  type StorageListQuery,
  type StorageResourceRef,
} from '../../../../types/azureStorage';
import type { StorageExplorerMode, StorageExplorerView } from '../../core/types/workspaceTypes';

export const STORAGE_QUERY_ROOT = 'azure-storage' as const;

export function storageConnectionQueryPrefix(connectionId: string): readonly [typeof STORAGE_QUERY_ROOT, string] {
  return [STORAGE_QUERY_ROOT, connectionId] as const;
}

/** Stable JSON regardless of property insertion order, so equal queries share one cache entry. */
export function storageQueryFingerprint(query: StorageListQuery): string {
  const record = query as Record<string, unknown>;
  const ordered: Record<string, unknown> = {};
  for (const key of Object.keys(record).sort()) {
    if (record[key] !== undefined) ordered[key] = record[key];
  }
  return JSON.stringify(ordered);
}

export function storageListingQueryKey(
  connectionId: string,
  resource: StorageResourceRef,
  query: StorageListQuery,
): readonly [typeof STORAGE_QUERY_ROOT, string, string, string, string] {
  return [STORAGE_QUERY_ROOT, connectionId, containerScopeKey(resource), storageResourceKey(resource), storageQueryFingerprint(query)] as const;
}

/** The single-blob surface (a blob-scoped attachment) keys its `get-properties` read by the same five-element shape with `'properties'` as the fingerprint. */
export function storageBlobPropertiesQueryKey(
  connectionId: string,
  resource: Extract<StorageResourceRef, { kind: 'blob' }>,
): readonly [typeof STORAGE_QUERY_ROOT, string, string, string, 'properties'] {
  return [STORAGE_QUERY_ROOT, connectionId, containerScopeKey(resource), storageResourceKey(resource), 'properties'] as const;
}

const QUEUE_SERVICE_REF = { kind: 'service', service: 'queue' } as const;
const QUEUES_WITH_COUNTS_QUERY = { kind: 'queues', includeCounts: true } as const;

/** Shared Object Explorer snapshot for the Queues folder and queue-pin reconciliation. */
export function storageQueuesQueryKey(connectionId: string) {
  return storageListingQueryKey(connectionId, QUEUE_SERVICE_REF, QUEUES_WITH_COUNTS_QUERY);
}

/** Queue peeks share the pinned five-element key; count is data identity, decode mode is display-only. */
export function storageQueuePeekQueryKey(
  connectionId: string,
  resource: Extract<StorageResourceRef, { kind: 'queue' }>,
  count: number,
) {
  return storageListingQueryKey(connectionId, resource, { kind: 'messages', mode: 'peek', count });
}

export function storageContainerPropertiesQueryKey(
  connectionId: string,
  resource: Extract<StorageResourceRef, { kind: 'container' }>,
): readonly [typeof STORAGE_QUERY_ROOT, string, string, string, 'properties'] {
  return [STORAGE_QUERY_ROOT, connectionId, containerScopeKey(resource), storageResourceKey(resource), 'properties'] as const;
}

export function storageContainerAclQueryKey(
  connectionId: string,
  resource: Extract<StorageResourceRef, { kind: 'container' }>,
): readonly [typeof STORAGE_QUERY_ROOT, string, string, string, 'acl'] {
  return storagePolicyAclQueryKey(connectionId, resource);
}

/** The ACL of any policy-bearing resource (container, table, queue — follow-up 2026-09-25; share — S7); the container's key is this one. */
export function storagePolicyAclQueryKey(
  connectionId: string,
  resource: Extract<StorageResourceRef, { kind: 'container' | 'table' | 'queue' | 'share' }>,
): readonly [typeof STORAGE_QUERY_ROOT, string, string, string, 'acl'] {
  return [STORAGE_QUERY_ROOT, connectionId, containerScopeKey(resource), storageResourceKey(resource), 'acl'] as const;
}

const FILE_SERVICE_REF = { kind: 'service', service: 'file' } as const;
const SHARES_QUERY = { kind: 'shares' } as const;

/** S7: the Object Explorer's File Shares snapshot (tree listing and share-pin resolution). */
export function storageSharesQueryKey(connectionId: string) {
  return storageListingQueryKey(connectionId, FILE_SERVICE_REF, SHARES_QUERY);
}

type ShareKeyRef = Extract<StorageResourceRef, { kind: 'share' }>;
type FileLocationRef = Extract<StorageResourceRef, { kind: 'share' | 'directory' | 'file' }>;

/** S7: `azure-storage:share-properties` of one share (snapshot identity kept), the five-element shape. */
export function storageSharePropertiesQueryKey(connectionId: string, share: ShareKeyRef): readonly [typeof STORAGE_QUERY_ROOT, string, string, string, 'properties'] {
  return [STORAGE_QUERY_ROOT, connectionId, containerScopeKey(share), storageResourceKey(share), 'properties'] as const;
}

/** S7: `azure-storage:share-snapshots` of one live share. */
export function storageShareSnapshotsQueryKey(connectionId: string, share: ShareKeyRef): readonly [typeof STORAGE_QUERY_ROOT, string, string, string, 'snapshots'] {
  return [STORAGE_QUERY_ROOT, connectionId, containerScopeKey(share), storageResourceKey(share), 'snapshots'] as const;
}

/** S7-08: `azure-storage:share-protocol` of a tab root (share / directory / file). */
export function storageShareProtocolQueryKey(connectionId: string, root: FileLocationRef): readonly [typeof STORAGE_QUERY_ROOT, string, string, string, 'protocol'] {
  return [STORAGE_QUERY_ROOT, connectionId, containerScopeKey(root), storageResourceKey(root), 'protocol'] as const;
}

/** S7-09: `azure-storage:file-properties` of a single-file tab root. */
export function storageFileItemPropertiesQueryKey(connectionId: string, file: Extract<StorageResourceRef, { kind: 'file' }>): readonly [typeof STORAGE_QUERY_ROOT, string, string, string, 'properties'] {
  return [STORAGE_QUERY_ROOT, connectionId, containerScopeKey(file), storageResourceKey(file), 'properties'] as const;
}

/** Page sizes per spec §3.3 (≤ 5000 blobs / containers, ≤ 1000 entities, ≤ 32 messages). */
export function storageListPageSize(mode: StorageExplorerMode): number {
  switch (mode) {
    case 'entities':
      return 1000;
    case 'messages':
      return 32;
    case 'blobs':
    case 'files':
    case 'hns':
      return 5000;
  }
}

/**
 * The listing query for the current location. `resource` (the IPC request's
 * location: container | blobPrefix) carries the folder; `prefix` here is the
 * user's prefix filter RELATIVE to that location — the main process appends it
 * to the location's own prefix. Blobs and (S7) file-share directories list here;
 * entity / message / HNS listings live elsewhere or arrive with their slices and
 * answer `null`, which the hook treats as "nothing to list". A `blob` resource (single-blob attachment)
 * is `null` here too — it is served by `useSingleBlobItem`, never by a listing.
 */
export function buildStorageListQuery(
  mode: StorageExplorerMode,
  resource: StorageResourceRef,
  view: StorageExplorerView,
): StorageListQuery | null {
  const filter = view.prefixFilter.trim();
  // S7: a share / folder lists one directory level; a single-file root reads its properties instead (S7-09).
  if (mode === 'files') return resource.kind === 'share' || resource.kind === 'directory' ? { kind: 'directoryEntries', ...(filter ? { prefix: filter } : {}) } : null;
  // S8: a Data Lake location (a directory, or a container = the file-system root) lists over DFS; `prefix` = DFS `startFrom`.
  if (mode === 'hns') {
    if (!(resource.kind === 'hnsPath' && resource.isDirectory) && resource.kind !== 'container') return null;
    return { kind: 'hnsPaths', recursive: view.listing === 'flat', ...(filter ? { prefix: filter } : {}) };
  }
  if (mode !== 'blobs') return null;
  if (resource.kind !== 'container' && resource.kind !== 'blobPrefix') return null;
  return {
    kind: 'blobs',
    listing: view.listing,
    ...(filter ? { prefix: filter } : {}),
    ...(view.showSnapshots ? { includeSnapshots: true } : {}),
    ...(view.showVersions ? { includeVersions: true } : {}),
    ...(view.showDeleted ? { includeDeleted: true } : {}),
  };
}
